import { useCallback, useEffect, useMemo, useState } from 'react'
import { loadCanvasImage } from '@/lib/canvasImages'
import { setImages } from '@/lib/setImages'
import type { WallpaperLayout } from '@/lib/scheduleWallpaper'
import type { Artist } from '@/types'

const FONT_FACES = ['400 16px "Geist Variable"', '500 16px "Geist Variable"', '600 16px "Geist Variable"']
const LOGO_URL = '/favicon-round.svg'

let logoPromise: Promise<HTMLImageElement | null> | null = null
function loadLogo(): Promise<HTMLImageElement | null> {
  logoPromise ??= new Promise(resolve => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = LOGO_URL
  })
  return logoPromise
}

function loadFonts(): Promise<unknown> {
  return Promise.all(FONT_FACES.map(face => document.fonts.load(face))).catch(() => {})
}

/**
 * What the wallpaper drawing needs beyond the layout: the photos of the sets
 * it shows, the logo, and the web font a canvas only draws once it's loaded.
 * The preview redraws as each arrives; `whenReady` is what an export awaits.
 */
export function useWallpaperAssets(layout: WallpaperLayout, artists: Record<string, Artist>) {
  const urls = useMemo(
    () => [...new Set(layout.blocks.map(b => setImages(b.set, artists).imageUrl).filter((u): u is string => !!u))],
    [layout.blocks, artists],
  )
  const [images, setImageMap] = useState<ReadonlyMap<string, ImageBitmap>>(() => new Map())
  const [logo, setLogo] = useState<HTMLImageElement | null>(null)
  const [fontsReady, setFontsReady] = useState(false)

  useEffect(() => {
    let live = true
    void loadLogo().then(img => { if (live) setLogo(img) })
    void loadFonts().then(() => { if (live) setFontsReady(true) })
    return () => { live = false }
  }, [])

  useEffect(() => {
    let live = true
    for (const url of urls) {
      void loadCanvasImage(url).then(bitmap => {
        if (live && bitmap) setImageMap(prev => (prev.get(url) === bitmap ? prev : new Map(prev).set(url, bitmap)))
      })
    }
    return () => { live = false }
  }, [urls])

  const whenReady = useCallback(async () => {
    const [bitmaps, img] = await Promise.all([Promise.all(urls.map(loadCanvasImage)), loadLogo(), loadFonts()])
    const map = new Map<string, ImageBitmap>()
    urls.forEach((url, i) => {
      const bitmap = bitmaps[i]
      if (bitmap) map.set(url, bitmap)
    })
    return { images: map as ReadonlyMap<string, ImageBitmap>, logo: img }
  }, [urls])

  return { images, logo, fontsReady, whenReady }
}
